import React, { useState, useEffect, useCallback, useRef } from "react";
import IconButton from "components/common/iconButton";
import { useTranslation } from "react-i18next"; // Import useTranslation hook
import { Link, useNavigate } from "react-router-dom";
import {
  Card,
  Col,
  Row,
  Button,
  Modal,
  Spinner,
} from "react-bootstrap";
import SubtleBadge from "components/common/subtleBadge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AdvanceTable from "components/common/advance-table/advanceTable";
import AdvanceTableSearchBox from "components/common/advance-table/advanceTableSearchBox";
import ManualPaginationFooter from "components/common/advance-table/manualPaginationFooter";
import useServerAdvanceTable from "hooks/useServerAdvanceTable";
import AdvanceTableProvider from "providers/advanceTableProvider";
import corner4 from "assets/img/illustrations/corner-4.png";
import Background from "components/common/background";
import paths from "routes/paths";
import FalconCloseButton from "components/common/falconCloseButton";
import axios from "axios"; // Import Axios
import { toast } from "react-toastify";
import { useAppContext } from '../../providers/appProvider';
import { fetchRoleAndClaims, handleLoadingAndAccess, getInitialPageSize, portalStatusBadge, getTranslatedSalutation } from "../../assets/js/common";
import BulkImportDrawer from '../common/bulkImportDrawer';
import { relationsImportConfig } from '../../config/importConfigs/relationsImportConfig';


// Function to delete relation
const deleteRelation = async (relationId) => {
  try {
    const response = await axios.delete(
      `${process.env.REACT_APP_API_URL}/relationRoute/deleteRelation/${relationId}`,{
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
    );
    if (response.status === 200) {
      toast.success(t("relationDeletedSuccessMessgae"), {
        position: "top-right",
        autoClose: 5000,
      });
      setTimeout(() => {
        window.location.reload();
      }, 300);
    }
  } catch (error) {
    console.error(
      "Error deleting relation:",
      error.response ? error.response.data : error.message
    );
  }
};

// Modal component for deletion confirmation
const StaticBackdropModal = ({ show, handleClose, relationId }) => {
  const { t } = useTranslation(); // Access translation function

  return (
    <Modal show={show} onHide={handleClose} backdrop="static">
      <Modal.Header>
        <Modal.Title>{t("confirmation")}</Modal.Title>
        <FalconCloseButton onClick={handleClose} />
      </Modal.Header>
      <Modal.Body>{t("confirmDeleteRelation")}</Modal.Body>
      <Modal.Footer>
        <Button variant="falcon-default" onClick={handleClose}>
          {t("cancel")}
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            deleteRelation(relationId);
            handleClose();
          }}
        >
          {t("delete")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Main component
const Relations = () => {
  const { t, i18n } = useTranslation(); // Access translation function
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true); // State for loading
  const [showModal, setShowModal] = useState(false);
  const [selectedRelationId, setSelectedRelationId] = useState(null);
  const navigate = useNavigate(); // Initialize useNavigate hook
  const [globalFilter, setGlobalFilter] = useState("");
  const [tableState, setTableState] = useState(() => ({
    pageIndex: 0,
    pageSize: getInitialPageSize("relationsTable"),
    sortKey: "",
    search: "",
  }));
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const { config } = useAppContext();
  const locationId = config.location?.LocationId;
  const customerId = localStorage.getItem("customerId");
  const [hasAccess, setHasAccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const latestRequestId = useRef(0);
  const abortControllerRef = useRef(null);

  const relationsConfig = relationsImportConfig();

  const handleClose = () => setShowModal(false);
  const handleShow = (relationId) => {
    setSelectedRelationId(relationId);
    setShowModal(true);
  };

  const handleViewDetails = (relationId) => {
    navigate(
      paths.relationDetails.replace(
        ":relationId",
        relationId.toLowerCase()
      )
    );
  };

  useEffect(() => {
    fetchRoleAndClaims({
      navigate,
      claimType: "relations",
      claimValue: "view",
      setHasAccess,
      setIsLoading,
    });
  }, [navigate]);

  useEffect(() => {
    setTableState((current) =>
      current.pageIndex === 0 ? current : { ...current, pageIndex: 0 }
    );
  }, [locationId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setTableState((current) => current.search === globalFilter
        ? current
        : { ...current, pageIndex: 0, search: globalFilter });
    }, 400);
    return () => clearTimeout(timer);
  }, [globalFilter]);
  
  const columns = [
    {
      accessorKey: "RelationNumber",
      header: t("number"),
      sortingFn: () => 0,
      cell: ({ row: { original } }) => <span>{original.RelationNumber}</span>
    },
    {
      accessorKey: "Name",
      header: t("name"),
      sortingFn: () => 0,
      cell: ({ row: { original } }) => <span>{original.Name}</span>,
    },
    {
      accessorKey: "Emailaddress1",
      header: t("email"),
      sortingFn: () => 0,
      cell: ({ row: { original } }) => <span>{original.Emailaddress1}</span>,
    },
    {
      accessorKey: "PostCode",
      header: t("postcode"),
      sortingFn: () => 0,
      cell: ({ row: { original } }) => <span>{original.PostCode}</span>,
    },
    {
      accessorKey: "City",
      header: t("city"),
      sortingFn: () => 0,
      cell: ({ row: { original } }) => <span>{original.City}</span>,
    },
    {
      accessorKey: "Employee",
      header: t("contactPerson"),
      enableSorting: false,
      cell: ({ row: { original } }) => <span>{getTranslatedSalutation(original, t)}</span>,
    },
    {
      accessorKey: "ApserionId",
      header: t("asperionRelationId"),
      enableSorting: false,
      cell: ({ row: { original } }) => original.AsperionRelationId ? <span>{original.AsperionRelationId}</span> : "--",
    },
    {
      accessorKey: "Type",
      header: t("type"),
      sortingFn: () => 0,
      cell: ({ row: { original } }) => (
        <SubtleBadge
          pill
          bg={original.Type === "0" ? "success" : "primary"}
          className="d-flex flex-center"
        >
          <p className="mb-0 data-font-size">{original.Type === "0" ? t("client") : t("prospect")}</p>
        </SubtleBadge>
      ),
    },
    {
      accessorKey: "Status",
      header: t("status"),
      sortingFn: () => 0,
      cell: ({ row: { original } }) => (
        <SubtleBadge
          pill
          bg={original.Status === "0" ? "success" : "danger"}
          className="d-flex flex-center"
        >
          <p className="mb-0 data-font-size">
            {original.Status === "0" ? `${t("active")}` : `${t("inactive")}`}
          </p>
          <FontAwesomeIcon
            icon={original.Status === "0" ? "check" : "ban"}
            transform="shrink-2"
            className="ms-1"
          />
        </SubtleBadge>
      ),
    },
    {
      accessorKey: "PortalStatus",
      header: t("customerPortalLable"),
      sortingFn: () => 0,
      cell: ({ row: { original } }) => portalStatusBadge(original.PortalStatus, t),
    },
  ];

  const { pageIndex, pageSize, search } = tableState;
  const lowerSearch = tableState.search.toLowerCase();
  const matchType = lowerSearch
    ? [
        ...(t("client").toLowerCase().includes(lowerSearch) ? ["client"] : []),
        ...(t("prospect").toLowerCase().includes(lowerSearch) ? ["prospect"] : []),
      ].join(",")
    : "";
  const matchStatus = lowerSearch
    ? [
        ...(t("active").toLowerCase().includes(lowerSearch) ? ["active"] : []),
        ...(t("inactive").toLowerCase().includes(lowerSearch) ? ["inactive"] : []),
      ].join(",")
    : "";
  const table = useServerAdvanceTable({
    data,
    columns,
    selection: false,
    sortable: true,
    pagination: true,
    manualPagination: true,
    pageCount: totalPages,
    paginationState: { pageIndex: tableState.pageIndex, pageSize: tableState.pageSize },
    onPaginationChange: (updater) => {
      setTableState((current) => {
        const pagination = typeof updater === "function"
          ? updater({ pageIndex: current.pageIndex, pageSize: current.pageSize })
          : updater;
        return {
          ...current,
          pageIndex: pagination.pageSize !== current.pageSize ? 0 : pagination.pageIndex,
          pageSize: pagination.pageSize,
        };
      });
    },
  });

  const sorting = table.getState().sorting;
  const sortBy = sorting[0]?.id;
  const sortOrder = sorting[0] ? (sorting[0].desc ? "desc" : "asc") : "";
  const sortKey = sortBy ? `${sortBy}:${sortOrder}` : "";

  if (tableState.sortKey !== sortKey) {
    setTableState((current) => ({ ...current, pageIndex: 0, sortKey }));
  }

  const fetchRelations = useCallback(async () => {
    setLoading(true);
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const requestId = ++latestRequestId.current;

    if (!locationId || !customerId) {
      console.error("LocationId and CustomerId are missing");
      setData([]);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/relationRoute/relationsPaginated`,
        {
          params: {
            locationId,
            customerId,
            page: pageIndex + 1,
            pageSize,
            search,
            ...(matchType ? { matchType } : {}),
            ...(matchStatus ? { matchStatus } : {}),
            ...(sortBy ? { sortBy, sortOrder } : {}),
          },
          headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
          signal: controller.signal,
        }
      );

      if (requestId !== latestRequestId.current) return;
      setData(response.data.relations || []);
      setTotalPages(response.data.totalPages || 0);
      setTotalCount(response.data.totalCount || 0);
    } catch (error) {
      if (axios.isCancel(error) || error.name === "CanceledError") return;
      if (requestId === latestRequestId.current) {
        if (error.response?.status === 401) {
          console.error("Unauthorized: Please log in again.");
        } else {
          console.error("Error fetching relations:", error);
        }
      }
    } finally {
      if (requestId === latestRequestId.current) setLoading(false);
    }
  }, [locationId, customerId, pageIndex, pageSize, search, sortBy, sortOrder, matchType, matchStatus]);

  useEffect(() => {
    fetchRelations();
  }, [fetchRelations]);

  useEffect(() => () => abortControllerRef.current?.abort(), []);

  const loadingOrRedirect = handleLoadingAndAccess(
    isLoading,
    hasAccess,
  );

  if (loadingOrRedirect) {
    return loadingOrRedirect; // Render the loading spinner or handle navigation
  }

  return (
    <>
      <AdvanceTableProvider {...table}>
        <Row className="g-3 mb-3">
          <Col lg={12} xxl={12}>
            <Card className="mb-3 h-100">
              <Background image={corner4} className="bg-card" />
              <Card.Body className="position-relative pe-3 pb-2">
                <Row className="flex-between-center">
                  <Col md>
                    <h5 className="mb-2 mb-md-0">{t("relation")}</h5>
                  </Col>
                  <Col xs="auto" className="d-flex gap-2">
                    <BulkImportDrawer 
                      config={relationsConfig}
                      onImportComplete={fetchRelations}
                    />
                    <IconButton
                      as={Link}
                      to={paths.newRelation}
                      variant="primary"
                      size="sm"
                      icon="plus"
                      transform="shrink-3"
                      data-tour="new-relation-btn"
                    >
                      <span className="ms-1">{t("newrelation")}</span>
                    </IconButton>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Card className="mb-3">
          <Card.Header>
            <Row className="flex-between-center">
              <Col xs={8} sm="auto" className="ms-auto text-end ps-0">
                <AdvanceTableSearchBox setGlobalFilter={setGlobalFilter} />
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="p-0">
            {loading ? ( // Show loading spinner while fetching data
              <div className="text-center p-4">
                <Spinner animation="border" role="status" />
                <span className="ms-2">{t("loading")}</span>
              </div>
            ) : (
              <>
                <AdvanceTable
                  headerClassName="bg-200 text-nowrap align-middle text-center"
                  rowClassName="align-middle white-space-nowrap"
                  tableProps={{
                    bordered: true,
                    striped: true,
                    size: "sm",
                    className: "mb-0 overflow-hidden data-font-size table-hover",
                  }}
                  getRowProps={(row) => ({
                    onClick: () => handleViewDetails(row.original.RelationId),
                    style: { cursor: "pointer" }
                  })}
                />
                {/* Always show headers, even if no data */}
                {data.length === 0 ? (
                  <div className="text-center pt-4">
                    {t("noDataAvailableText")}
                  </div>
                ) : null}
              </>
            )}
          </Card.Body>
          <Card.Footer>
            {/* <AdvanceTablePagination /> */}
            <ManualPaginationFooter
              totalCount={totalCount}
              pageIndex={tableState.pageIndex}
              pageSize={tableState.pageSize}
              onPageSizeChange={(pageSize) => {
                setTableState((current) => ({ ...current, pageIndex: 0, pageSize }));
              }}
              tableKey="relationsTable"
            />
          </Card.Footer>
        </Card>
      </AdvanceTableProvider>

      {/* Modal for deletion confirmation */}
      <StaticBackdropModal
        show={showModal}
        handleClose={handleClose}
        relationId={selectedRelationId}
      />
    </>
  );
};

export default Relations;